import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, subDays, subMonths, startOfMonth, endOfMonth, differenceInCalendarDays, isSameDay, isSameMonth } from 'date-fns';
import { toast } from 'sonner';
import { Store, CalendarIcon, ChevronsUpDown, Check, PackageSearch, Printer, Download, FileSpreadsheet, ClipboardList, X, Layers, Plus, History, Wand2 } from 'lucide-react';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import SupplierStockTable from '@/components/SupplierStockTable';
import StockDialog from '@/components/StockDialog';
import SupplierReminders from '@/components/SupplierReminders';
import { triggerReminderPrompt } from '@/components/ReminderPrompt';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn, fmtDate } from '@/lib/utils';

function friendlyWindowLabel(from, to) {
  const today = new Date();
  const span = differenceInCalendarDays(to, from) + 1;
  const dist = differenceInCalendarDays(today, to);
  if (isSameDay(from, startOfMonth(from)) && isSameMonth(from, to)) {
    if (isSameMonth(from, today)) return 'This month';
    if (isSameDay(to, endOfMonth(from))) {
      if (isSameMonth(from, subMonths(today, 1))) return 'Last month';
      return format(from, 'MMMM yyyy');
    }
  }
  if (dist === 0) return `Last ${span} days`;
  if (span === 7 && dist % 7 === 0) return dist === 7 ? 'Previous week' : `${dist / 7} weeks back`;
  if (span === 15 && dist % 15 === 0) return dist === 15 ? 'Previous 15 days' : `${dist}–${dist + span - 1} days back`;
  return 'Custom range';
}

function DatePickerBtn({ value, onChange, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid={testid} variant="outline" className="h-8 rounded-none border-zinc-300 justify-start text-left font-normal text-xs px-2 hover:bg-zinc-50 w-[118px]">
          <CalendarIcon className="mr-1.5 h-3 w-3 text-zinc-400 shrink-0" />
          {value ? format(value, 'dd-MM-yyyy') : <span className="text-zinc-400">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-none" align="start">
        <Calendar mode="single" selected={value} onSelect={(d) => { onChange(d); setOpen(false); }} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

export default function SupplierViewPage() {
  const [searchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState(searchParams.get('supplier') || null);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState(new Date());
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderQty, setOrderQty] = useState({});
  const [stockProduct, setStockProduct] = useState(null);
  const reminderCountRef = useRef(0);
  const handleReminderCount = useCallback((n) => { reminderCountRef.current = n; }, []);

  // Navigate-away / supplier-switch prompt (reminders are NEVER auto-completed)
  useEffect(() => {
    reminderCountRef.current = 0;
    return () => {
      if (supplier && reminderCountRef.current > 0) triggerReminderPrompt(supplier);
    };
  }, [supplier]);
  const [windows, setWindows] = useState([]);
  const [preset, setPreset] = useState('');

  useEffect(() => { setOrderQty({}); }, [supplier]);

  const activeWindows = useMemo(() => windows.filter((w) => w.from && w.to), [windows]);
  const windowsKey = useMemo(
    () => activeWindows.map((w) => `${format(w.from, 'yyyy-MM-dd')}_${format(w.to, 'yyyy-MM-dd')}`).join('|'),
    [activeWindows]
  );

  const applyPreset = (value) => {
    setPreset(value);
    const today = new Date();
    let wins = [];
    if (value === 'month') {
      for (let i = 0; i < 3; i++) {
        const ref = subMonths(today, i);
        wins.push({ from: startOfMonth(ref), to: i === 0 ? today : endOfMonth(ref) });
      }
    } else {
      const len = value === 'week' ? 7 : 15;
      for (let i = 0; i < 3; i++) {
        wins.push({ from: subDays(today, i * len + len - 1), to: subDays(today, i * len) });
      }
    }
    setWindows(wins);
  };

  const updateWindow = (idx, field, val) => {
    setWindows((prev) => prev.map((w, i) => (i === idx ? { ...w, [field]: val } : w)));
    setPreset('');
  };

  const removeWindow = (idx) => {
    setWindows((prev) => prev.filter((_, i) => i !== idx));
    setPreset('');
  };

  useEffect(() => {
    api.get('/suppliers').then(({ data }) => setSuppliers(data)).catch(() => {});
  }, []);

  const fetchView = useCallback(async () => {
    if (!supplier) return;
    setLoading(true);
    try {
      const params = { supplier };
      if (dateFrom) params.date_from = format(dateFrom, 'yyyy-MM-dd');
      if (dateTo) params.date_to = format(dateTo, 'yyyy-MM-dd');
      activeWindows.forEach((w, i) => {
        params[`w${i + 1}_from`] = format(w.from, 'yyyy-MM-dd');
        params[`w${i + 1}_to`] = format(w.to, 'yyyy-MM-dd');
      });
      const { data } = await api.get('/supplier-view', { params });
      setData(data);
    } catch {
      toast.error('Failed to load supplier view');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier, dateFrom, dateTo, windowsKey]);

  useEffect(() => { fetchView(); }, [fetchView]);

  const summary = data?.summary;

  const compareWindows = useMemo(() =>
    (data?.windows || []).map((w, i) => ({
      key: i + 1,
      label: friendlyWindowLabel(new Date(w.from), new Date(w.to)),
      dates: `${format(new Date(w.from), 'dd-MM-yyyy')} – ${format(new Date(w.to), 'dd-MM-yyyy')}`,
      prev: w.prev_from ? `${format(new Date(w.prev_from), 'dd-MM-yyyy')} – ${format(new Date(w.prev_to), 'dd-MM-yyyy')}` : ''
    })),
    [data]
  );

  const orderItems = useMemo(() =>
    (data?.products || [])
      .filter((p) => Number(orderQty[p.barcode]) > 0)
      .map((p) => ({ product_name: p.product_name, barcode: p.barcode, mrp: p.mrp, order_qty: Number(orderQty[p.barcode]) })),
    [data, orderQty]
  );
  const totalUnits = useMemo(() => orderItems.reduce((sum, i) => sum + i.order_qty, 0), [orderItems]);

  const [schedOpen, setSchedOpen] = useState(false);
  const [schedFreq, setSchedFreq] = useState('weekly');
  const [schedDay, setSchedDay] = useState('monday');
  const [schedSaving, setSchedSaving] = useState(false);

  const saveSchedule = async () => {
    setSchedSaving(true);
    try {
      await api.put('/suppliers/schedule', { supplier, frequency: schedFreq, visit_day: schedDay, last_visit_date: null, notes: '' });
      toast.success(`Visit schedule saved for ${supplier}`);
      setSchedOpen(false);
      fetchView();
    } catch {
      toast.error('Failed to save schedule');
    } finally {
      setSchedSaving(false);
    }
  };

  const prefillSuggestions = () => {
    const sugg = (data?.products || []).filter((p) => p.suggested_qty > 0);
    if (!sugg.length) {
      toast.info('No suggestions available yet — they appear once this supplier has sales history');
      return;
    }
    setOrderQty(Object.fromEntries(sugg.map((p) => [p.barcode, String(p.suggested_qty)])));
    toast.success(`Prefilled ${sugg.length} products to cover the next ${data?.suggestion_horizon_days || 7} days`);
  };

  const offerMarkVisited = () => {
    toast(`Order prepared for ${supplier}`, {
      description: 'Mark today as their visit date in the calendar?',
      duration: 8000,
      action: {
        label: 'Set visit today',
        onClick: async () => {
          try {
            await api.post(`/suppliers/${encodeURIComponent(supplier)}/mark-visited`);
            toast.success(`Visit date updated for ${supplier}`);
            fetchView();
          } catch {
            toast.error('Could not update visit date');
          }
        }
      }
    });
  };

  const handleOrderExport = async (fmt) => {
    try {
      const response = await api.post(`/order-sheet/export?format=${fmt}`, { supplier, items: orderItems }, { responseType: 'blob' });
      const cd = response.headers['content-disposition'];
      let filename = `order_sheet.${fmt === 'excel' ? 'xlsx' : 'csv'}`;
      const match = cd?.match(/filename=(.+)/);
      if (match) filename = match[1];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Order sheet downloaded (${orderItems.length} items)`);
      offerMarkVisited();
    } catch {
      toast.error('Failed to export order sheet');
    }
  };

  const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const openPrintWindow = (title, tableHtml, metaHtml) => {
    const html = `<!DOCTYPE html><html><head><title>${esc(title)}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#18181b}
      h1{font-size:18px;margin:0 0 4px}
      .meta{font-size:12px;color:#52525b;margin-bottom:16px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #d4d4d8;padding:6px 8px;text-align:left}
      th{background:#f4f4f5;text-transform:uppercase;font-size:10px;letter-spacing:.05em}
      td.num,th.num{text-align:right;font-family:monospace}
      .sign{margin-top:40px;font-size:12px;display:flex;justify-content:space-between}
      .sign span{border-top:1px solid #a1a1aa;padding-top:6px;width:200px;text-align:center}
    </style></head><body>
      <h1>OrderGenie — ${esc(title)}</h1>
      <div class="meta">${metaHtml}</div>
      ${tableHtml}
      <div class="sign"><span>Prepared by</span><span>Supplier signature</span></div>
    </body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const w = window.open(url, '_blank');
    if (!w) {
      URL.revokeObjectURL(url);
      toast.error('Popup blocked — allow popups to print');
      return;
    }
    setTimeout(() => {
      w.focus();
      w.print();
      URL.revokeObjectURL(url);
    }, 500);
  };

  const handlePrintOrder = () => {
    const rows = orderItems.map((i, idx) =>
      `<tr><td class="num">${idx + 1}</td><td>${esc(i.product_name)}</td><td class="num">${esc(i.barcode) || '-'}</td><td class="num">${i.mrp > 0 ? i.mrp : '-'}</td><td class="num"><strong>${i.order_qty}</strong></td></tr>`
    ).join('');
    openPrintWindow(
      'Purchase Order',
      `<table><thead><tr><th class="num">#</th><th>Product</th><th class="num">Barcode</th><th class="num">MRP</th><th class="num">Order Qty</th></tr></thead><tbody>${rows}</tbody></table>`,
      `Supplier: <strong>${esc(supplier)}</strong> &nbsp;|&nbsp; Date: ${format(new Date(), 'dd-MM-yyyy')} &nbsp;|&nbsp; ${orderItems.length} items, ${totalUnits} units`
    );
    offerMarkVisited();
  };

  const handlePrintTable = () => {
    const products = data?.products || [];
    const rows = products.map((p, idx) =>
      `<tr><td class="num">${idx + 1}</td><td>${esc(p.product_name)}</td><td class="num">${esc(p.barcode) || '-'}</td><td class="num">${p.mrp > 0 ? p.mrp : '-'}</td><td class="num">${p.period_purchased}</td><td class="num">${p.period_sold}</td><td class="num">${fmtDate(p.last_sold_date) || '-'}</td><td class="num">${fmtDate(p.last_purchased_date) || '-'}</td></tr>`
    ).join('');
    openPrintWindow(
      'Supplier Stock Sheet',
      `<table><thead><tr><th class="num">#</th><th>Product</th><th class="num">Barcode</th><th class="num">MRP</th><th class="num">Purchased</th><th class="num">Sold</th><th class="num">Last Sold</th><th class="num">Last Purchased</th></tr></thead><tbody>${rows}</tbody></table>`,
      `Supplier: <strong>${esc(supplier)}</strong> &nbsp;|&nbsp; Period: ${dateFrom ? format(dateFrom, 'dd-MM-yyyy') : 'start'} → ${dateTo ? format(dateTo, 'dd-MM-yyyy') : 'today'} &nbsp;|&nbsp; ${products.length} products`
    );
  };

  return (
    <div className="min-h-screen bg-white page-enter">
      <Navbar />
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 pb-24">
        {/* Selector row */}
        <div className="border border-zinc-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <Store className="h-4 w-4 text-emerald-600" />
            <span className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500">Supplier Stock View</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div className="lg:col-span-2">
              <label className="field-label">Supplier</label>
              <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="supplier-view-select"
                    variant="outline"
                    role="combobox"
                    className="w-full h-11 rounded-none border-zinc-300 justify-between text-left font-medium text-base hover:bg-zinc-50"
                  >
                    <span className={cn('truncate', !supplier && 'text-zinc-400 font-normal')}>
                      {supplier || 'Select a supplier...'}
                    </span>
                    <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[380px] p-0 rounded-none" align="start">
                  <Command>
                    <CommandInput placeholder="Type supplier name..." className="h-10 text-sm" />
                    <CommandList>
                      <CommandEmpty className="py-3 text-xs text-zinc-400 text-center">No supplier found.</CommandEmpty>
                      <CommandGroup>
                        {suppliers.map((s) => (
                          <CommandItem
                            key={s}
                            value={s}
                            onSelect={() => { setSupplier(s); setSupplierOpen(false); }}
                            className="rounded-none text-sm cursor-pointer"
                          >
                            <Check className={cn('mr-2 h-3.5 w-3.5', supplier === s ? 'opacity-100' : 'opacity-0')} />
                            {s}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="field-label">From</label>
              <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
                <PopoverTrigger asChild>
                  <Button data-testid="supplier-view-date-from" variant="outline" className="w-full h-11 rounded-none border-zinc-300 justify-start text-left font-normal text-sm hover:bg-zinc-50">
                    <CalendarIcon className="mr-2 h-3.5 w-3.5 text-zinc-400" />
                    {dateFrom ? format(dateFrom, 'dd-MM-yyyy') : <span className="text-zinc-400">Start date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none" align="start">
                  <Calendar mode="single" selected={dateFrom} onSelect={(d) => { setDateFrom(d); setDateFromOpen(false); }} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="field-label">To</label>
              <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
                <PopoverTrigger asChild>
                  <Button data-testid="supplier-view-date-to" variant="outline" className="w-full h-11 rounded-none border-zinc-300 justify-start text-left font-normal text-sm hover:bg-zinc-50">
                    <CalendarIcon className="mr-2 h-3.5 w-3.5 text-zinc-400" />
                    {dateTo ? format(dateTo, 'dd-MM-yyyy') : <span className="text-zinc-400">End date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none" align="start">
                  <Calendar mode="single" selected={dateTo} onSelect={(d) => { setDateTo(d); setDateToOpen(false); }} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            {data?.last_visit_date && (
              <div>
                <Button
                  data-testid="since-last-visit-btn"
                  variant="outline"
                  onClick={() => {
                    setDateFrom(new Date(`${data.last_visit_date}T00:00:00`));
                    setDateTo(new Date());
                    toast.info(`Showing activity since last visit (${fmtDate(data.last_visit_date)})`);
                  }}
                  className="w-full h-11 rounded-none border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 text-xs font-medium"
                  title={`Set period to ${fmtDate(data.last_visit_date)} → today`}
                >
                  <History className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                  <span className="truncate">Since last visit ({fmtDate(data.last_visit_date)})</span>
                </Button>
              </div>
            )}
          </div>

          {/* Compare windows row */}
          <div className="mt-4 pt-3 border-t border-zinc-100 flex items-end gap-3 flex-wrap">
            <div className="flex items-center gap-2 h-8">
              <Layers className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-500">Compare Windows</span>
            </div>
            <div>
              <Select value={preset} onValueChange={applyPreset}>
                <SelectTrigger data-testid="window-preset-select" className="rounded-none border-zinc-300 h-8 text-xs w-[150px]">
                  <SelectValue placeholder="Quick preset..." />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="week" className="rounded-none text-xs">Last week</SelectItem>
                  <SelectItem value="15days" className="rounded-none text-xs">Last 15 days</SelectItem>
                  <SelectItem value="month" className="rounded-none text-xs">Last month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {windows.map((w, idx) => (
              <div key={`window-slot-${idx}`} data-testid={`compare-window-${idx + 1}`} className="flex items-center gap-1.5 border border-amber-200 bg-amber-50/40 px-2 py-1.5">
                <span className="text-[10px] font-bold text-amber-700 font-mono">W{idx + 1}</span>
                <DatePickerBtn value={w.from} onChange={(d) => updateWindow(idx, 'from', d)} placeholder="From" testid={`window-${idx + 1}-from`} />
                <span className="text-zinc-400 text-xs">→</span>
                <DatePickerBtn value={w.to} onChange={(d) => updateWindow(idx, 'to', d)} placeholder="To" testid={`window-${idx + 1}-to`} />
                <button
                  data-testid={`window-${idx + 1}-remove`}
                  onClick={() => removeWindow(idx)}
                  className="text-zinc-400 hover:text-red-600 transition-colors ml-0.5"
                  title="Remove window"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {windows.length < 3 && (
              <Button
                data-testid="add-window-btn"
                variant="outline"
                size="sm"
                onClick={() => setWindows((prev) => [...prev, { from: null, to: null }])}
                className="rounded-none border-dashed border-zinc-300 h-8 text-xs text-zinc-500 hover:border-amber-400 hover:text-amber-700"
              >
                <Plus className="h-3 w-3 mr-1" /> Add window
              </Button>
            )}
            {windows.length > 0 && (
              <Button
                data-testid="clear-windows-btn"
                variant="ghost"
                size="sm"
                onClick={() => { setWindows([]); setPreset(''); }}
                className="rounded-none h-8 text-xs text-zinc-400 hover:text-zinc-700"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Pending reminders for this supplier */}
        {supplier && <SupplierReminders supplier={supplier} onCountChange={handleReminderCount} />}

        {/* Order cycle banner + add schedule */}
        {supplier && data && (data.order_cycle || !data.has_schedule) && (
          <div className="flex items-stretch gap-2 flex-wrap">
            {data.order_cycle && (
              <div
                data-testid="order-cycle-banner"
                className={`flex-1 min-w-[280px] flex items-center gap-2.5 border px-4 py-2.5 text-sm ${
                  data.order_cycle.due ? 'border-sky-300 bg-sky-50/70 text-sky-900' : 'border-zinc-200 bg-zinc-50/60 text-zinc-600'
                }`}
              >
                <History className={`h-4 w-4 shrink-0 ${data.order_cycle.due ? 'text-sky-600' : 'text-zinc-400'}`} />
                <span>
                  {data.order_cycle.due && <span className="font-bold text-sky-700">Probably due — </span>}
                  Last ordered <span className="font-mono font-semibold">{data.order_cycle.days_since} days</span> ago
                  ({fmtDate(data.order_cycle.last_ordered)}) · you usually order from this supplier every
                  <span className="font-mono font-semibold"> ~{data.order_cycle.cycle_days} days</span>
                </span>
              </div>
            )}
            {!data.has_schedule && (
              <Button
                data-testid="add-schedule-btn"
                variant="outline"
                onClick={() => setSchedOpen(true)}
                className="rounded-none border-dashed border-zinc-300 text-zinc-500 hover:border-emerald-400 hover:text-emerald-700 text-xs h-auto py-2.5"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add visit schedule
              </Button>
            )}
          </div>
        )}

        {/* Summary chips */}
        {supplier && summary && (
          <div data-testid="supplier-view-summary" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="border border-zinc-200 bg-white p-4">
              <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-400">Products</p>
              <p className="text-2xl font-bold font-mono text-zinc-900 mt-1">{summary.total_products.toLocaleString()}</p>
            </div>
            <div className="border border-zinc-200 bg-white p-4">
              <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-400">Sold (period)</p>
              <p className="text-2xl font-bold font-mono text-emerald-700 mt-1">{summary.period_sold.toLocaleString()}</p>
            </div>
            <div className="border border-zinc-200 bg-white p-4">
              <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-400">Purchased (period)</p>
              <p className="text-2xl font-bold font-mono text-sky-700 mt-1">{summary.period_purchased.toLocaleString()}</p>
            </div>
            <div className="border border-zinc-200 bg-white p-4">
              <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-400">Bought, Not Selling</p>
              <p className={`text-2xl font-bold font-mono mt-1 ${summary.not_selling_count > 0 ? 'text-red-600' : 'text-zinc-300'}`}>
                {summary.not_selling_count.toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {/* Table or empty state */}
        {supplier ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-zinc-400">Type an order quantity on any row to build the order sheet</p>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="prefill-suggested-btn"
                  variant="outline"
                  size="sm"
                  onClick={prefillSuggestions}
                  disabled={!data?.products?.length}
                  className="rounded-none border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800 h-8 text-xs"
                  title={`Fills order quantities from each product's weekly sales rhythm for the next ${data?.suggestion_horizon_days || 7} days, minus confirmed stock`}
                >
                  <Wand2 className="h-3 w-3 mr-1.5" /> Prefill Suggested
                </Button>
                <Button
                  data-testid="print-table-btn"
                  variant="outline"
                  size="sm"
                  onClick={handlePrintTable}
                  disabled={!data?.products?.length}
                  className="rounded-none border-zinc-300 h-8 text-xs"
                >
                  <Printer className="h-3 w-3 mr-1.5" /> Print Stock Sheet
                </Button>
              </div>
            </div>
            <SupplierStockTable
              products={data?.products || []}
              loading={loading}
              monthLabels={data?.month_labels}
              compareWindows={compareWindows}
              orderQty={orderQty}
              onOrderQtyChange={(barcode, val) => setOrderQty((prev) => ({ ...prev, [barcode]: val }))}
              onSetStock={setStockProduct}
            />
          </div>
        ) : (
          <div data-testid="supplier-view-empty" className="border border-zinc-200 bg-zinc-50/50 py-20 flex flex-col items-center gap-4">
            <PackageSearch className="h-10 w-10 text-zinc-300" />
            <div className="text-center">
              <p className="text-sm font-medium text-zinc-600">Select a supplier to begin</p>
              <p className="text-xs text-zinc-400 mt-1">You'll instantly see what they supplied vs what actually sold</p>
            </div>
          </div>
        )}
      </main>

      {/* Stock confirm/adjust dialog */}
      {stockProduct && (
        <StockDialog
          product={stockProduct}
          onOpenChange={(v) => { if (!v) setStockProduct(null); }}
          onSuccess={fetchView}
        />
      )}

      {/* Add visit schedule dialog */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="rounded-none sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-base">Visit schedule — {supplier}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="field-label block mb-1.5">How often does this supplier visit?</label>
              <Select value={schedFreq} onValueChange={setSchedFreq}>
                <SelectTrigger data-testid="sched-freq-select" className="rounded-none border-zinc-300 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="weekly" className="rounded-none">Weekly</SelectItem>
                  <SelectItem value="biweekly" className="rounded-none">Every 2 weeks</SelectItem>
                  <SelectItem value="monthly" className="rounded-none">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="field-label block mb-1.5">Which day does he usually come?</label>
              <Select value={schedDay} onValueChange={setSchedDay}>
                <SelectTrigger data-testid="sched-day-select" className="rounded-none border-zinc-300 h-9 capitalize">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((d) => (
                    <SelectItem key={d} value={d} className="rounded-none capitalize">{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSchedOpen(false)} className="rounded-none text-zinc-500">Cancel</Button>
            <Button data-testid="sched-save-btn" onClick={saveSchedule} disabled={schedSaving} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white">
              {schedSaving ? 'Saving…' : 'Save schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order sheet bar */}
      {orderItems.length > 0 && (
        <div data-testid="order-sheet-bar" className="fixed bottom-0 left-0 right-0 z-40 bg-zinc-900 border-t border-zinc-800 animate-fade-in">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 text-white">
              <ClipboardList className="h-4 w-4 text-emerald-400" />
              <span data-testid="order-sheet-count" className="text-sm font-semibold">
                {orderItems.length} {orderItems.length === 1 ? 'item' : 'items'}
              </span>
              <span className="text-xs text-zinc-400 font-mono">{totalUnits.toLocaleString()} units</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="order-clear-btn"
                variant="ghost"
                size="sm"
                onClick={() => setOrderQty({})}
                className="rounded-none h-8 text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
              >
                <X className="h-3 w-3 mr-1" /> Clear
              </Button>
              <Button
                data-testid="order-export-csv-btn"
                variant="outline"
                size="sm"
                onClick={() => handleOrderExport('csv')}
                className="rounded-none h-8 text-xs bg-transparent border-zinc-600 text-white hover:bg-zinc-800 hover:text-white"
              >
                <Download className="h-3 w-3 mr-1.5" /> CSV
              </Button>
              <Button
                data-testid="order-export-excel-btn"
                variant="outline"
                size="sm"
                onClick={() => handleOrderExport('excel')}
                className="rounded-none h-8 text-xs bg-transparent border-zinc-600 text-white hover:bg-zinc-800 hover:text-white"
              >
                <FileSpreadsheet className="h-3 w-3 mr-1.5" /> Excel
              </Button>
              <Button
                data-testid="order-print-btn"
                size="sm"
                onClick={handlePrintOrder}
                className="rounded-none h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Printer className="h-3 w-3 mr-1.5" /> Print Order
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
